import styled, { css, useTheme } from 'styled-components'
import ReactMarkdown from 'react-markdown'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'

import remarkGfm from 'remark-gfm'
import { useModal } from 'hooks'
import { memo } from 'react'

const YOUTUBE_REGEX = /^https:\/\/www\.youtube\.com\/watch\?v=([a-zA-Z0-9_-]+)&/
const IMAGE_REGEX = /\.(gif|jpe?g|tiff?|png|webp|bmp)$/i

const TOOLKIT_REGEX = /\/toolkits\/[^/]+/
const VOICE_REGEX = /\/integrations\/voice\/[^/]+/
const SETTINGS_REGEX = /\/integrations\?setting=([^/]+)/

const AiMessageMarkdown = ({
    isReply = false,
    children,
}: {
    isReply?: boolean
    children: string
}) => {
    const theme = useTheme()
    const { openModal } = useModal()

    return (
        <StyledReactMarkdown
            isReply={isReply}
            remarkPlugins={[[remarkGfm, { singleTilde: false }]]}
            components={{
                img: props => <StyledImg {...props} />,
                table: props => <StyledTable {...props} />,
                a: ({ href, children }) => {
                    if (YOUTUBE_REGEX.test(href as string)) {
                        const videoId = (href as string).match(YOUTUBE_REGEX)?.[1]
                        return (
                            <>
                                <br />
                                <iframe
                                    width='560'
                                    height='315'
                                    src={`https://www.youtube.com/embed/${videoId}`}
                                    title='YouTube video player'
                                    frameBorder='0'
                                    allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
                                    allowFullScreen
                                ></iframe>
                                <br />
                            </>
                        )
                    }

                    if (IMAGE_REGEX.test(href as string)) {
                        const imageUrl = href as string
                        return <StyledImg src={imageUrl} alt={children as string} />
                    }

                    if (SETTINGS_REGEX.test(href as string)) {
                        const regex = /\/integrations\?setting=([^/]+)/
                        const match: RegExpExecArray | null = regex.exec(href || '')
                        const slug = match ? match[1] : ''

                        return (
                            <button
                                onClick={() =>
                                    openModal({
                                        name: 'llm-settings-modal',
                                        data: { settingsSlug: slug },
                                    })
                                }
                            >
                                <StyledText>here</StyledText>
                            </button>
                        )
                    }
                    if (TOOLKIT_REGEX.test(href as string)) {
                        const inputString = href
                        const staticPart = '/toolkits/'
                        const extractedPart = inputString?.replace(staticPart, '')

                        return (
                            <button
                                onClick={() => {
                                    openModal({
                                        name: 'toolkit-modal',
                                        data: { toolSlug: extractedPart },
                                    })
                                }}
                            >
                                <StyledText>{children}</StyledText>
                            </button>
                        )
                    }

                    if (VOICE_REGEX.test(href as string)) {
                        const inputString = href
                        const staticPart = '/integrations/voice/'
                        const extractedPart = inputString?.replace(staticPart, '')

                        return (
                            <button
                                onClick={() => {
                                    openModal({
                                        name: 'voice-modal',
                                        data: { voiceSlug: extractedPart },
                                    })
                                }}
                            >
                                <StyledText>{children}</StyledText>
                            </button>
                        )
                    }

                    return <a href={href as string}>{children}</a>
                },
                code({ inline, className, children, style, ...props }) {
                    const match = /language-(\w+)/.exec(className || 'language-js')
                    return !inline && match ? (
                        <SyntaxHighlighter
                            style={theme?.body.markdownColor || style}
                            language={match[1]}
                            PreTag='div'
                            {...props}
                        >
                            {String(children).replace(/\n$/, '')}
                        </SyntaxHighlighter>
                    ) : (
                        <code className={className} {...props}>
                            {children}
                        </code>
                    )
                },
                h3: ({ children }) => {
                    // Custom h3 component to style specific text

                    return <StyledH3>{children}</StyledH3>
                },
            }}
        >
            {children}
        </StyledReactMarkdown>
    )
}

export default memo(AiMessageMarkdown)

const StyledReactMarkdown = styled(ReactMarkdown)<{ isReply: boolean }>`
    color: ${({ theme }) => theme.body.textColorPrimary};
    display: flex;
    flex-direction: column;
    gap: 10px;

    font-weight: 400;
    font-style: normal;
    font-size: 14px;

    word-wrap: break-word; // Break long words to fit within the container
    word-break: break-all; // Break long words at any character to fit within the container
    overflow-wrap: break-word;

    ${props =>
        props.isReply &&
        css`
            color: ${({ theme }) => theme.body.textColorSecondary};
            font-size: 12px;
        `};
`

const StyledTable = styled.table`
    border-collapse: collapse;

    th,
    td {
        border: 1px solid #fff;
        padding: 5px 30px;
        text-align: center;
    }
`
const StyledText = styled.span`
    text-decoration: underline;
`
const StyledImg = styled.img`
    margin-top: 5px;

    width: 300px;
    height: 300px;

    border-radius: 8px;
`
const StyledH3 = styled.span`
    color: ${({ theme }) => theme.body.textColorSecondary};
    font-size: 14px;
`
